import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { COLORS } from '../../utils/constants';
import { Save, Plus, Trash2, Upload, Download, FileText, Image as ImageIcon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = COLORS;

const QuestionForm = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const directFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    question_type: 'Board Exams',
    board_id: '',
    exam_id: '',
    class_id: '',
    subject_id: '',
    topic_id: '',
    sub_topic_id: ''
  });

  const [questions, setQuestions] = useState([{
    question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_answer: 'A', level: 'Any', answer_desc: '', format: 'MCQ', image: ''
  }]);
  
  const [boards, setBoards] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [b, ex, c, s, t, st] = await Promise.all([
          api.get('/config/board'),
          api.get('/config/exam'),
          api.get('/config/class'),
          api.get('/config/subject'),
          api.get('/config/topic'),
          api.get('/config/sub-topic')
        ]);
        setBoards(b.data.filter(x => x.status === 'active'));
        setExams(ex.data.filter(x => x.status === 'active'));
        setClasses(c.data.filter(x => x.status === 'active'));
        setSubjects(s.data.filter(x => x.status === 'active'));
        setTopics(t.data.filter(x => x.status === 'active'));
        setSubTopics(st.data.filter(x => x.status === 'active'));
      } catch (err) {
        toast.error('Failed to load configurations');
      }
    };
    fetchConfig();
  }, []);

  const addEmptyQuestion = () => {
    setQuestions([...questions, {
      question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
      correct_answer: 'A', level: 'Any', answer_desc: '', format: 'MCQ', image: ''
    }]);
  };

  const updateQuestion = (index, field, value) => {
    const newQ = [...questions];
    newQ[index][field] = value;
    setQuestions(newQ);
  };

  const removeQuestion = (index) => {
    const newQ = [...questions];
    newQ.splice(index, 1);
    setQuestions(newQ);
  };

  const handleImageUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const payload = new FormData();
    payload.append('image', file);
    try {
      const toastId = toast.loading('Uploading image...');
      const res = await api.post('/questions/upload-image', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateQuestion(idx, 'image', res.data.url);
      toast.success('Image uploaded successfully', { id: toastId });
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  const handleDirectUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!formData.topic_id || !formData.sub_topic_id) {
      toast.error("Please select a Topic and Sub Topic before uploading");
      return;
    }
    if (formData.question_type === 'Compititive' && !formData.exam_id) {
      toast.error("Please select an Exam before uploading");
      return;
    }

    const payload = new FormData();
    payload.append('file', file);
    payload.append('topic_id', formData.topic_id);
    payload.append('sub_topic_id', formData.sub_topic_id);
    if (formData.question_type === 'Compititive' && formData.exam_id) {
      payload.append('exam_id', formData.exam_id);
    }
    payload.append('save_immediately', 'true');

    try {
      const toastId = toast.loading('Uploading and saving questions directly to database...');
      const res = await api.post('/questions/upload', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message || 'Questions saved successfully!', { id: toastId });
      navigate('/config/questions');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to upload questions');
    } finally {
      if (directFileInputRef.current) directFileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!formData.topic_id || !formData.sub_topic_id) {
      toast.error("Please select a Topic and Sub Topic before uploading");
      return;
    }
    if (formData.question_type === 'Compititive' && !formData.exam_id) {
      toast.error("Please select an Exam before uploading");
      return;
    }

    const payload = new FormData();
    payload.append('file', file);
    payload.append('topic_id', formData.topic_id);
    payload.append('sub_topic_id', formData.sub_topic_id);
    if (formData.question_type === 'Compititive' && formData.exam_id) {
      payload.append('exam_id', formData.exam_id);
    }

    try {
      const toastId = toast.loading('Parsing questions into editor...');
      const res = await api.post('/questions/upload', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedQuestions = res.data.questions || [];
      if (uploadedQuestions.length > 0) {
        if (questions.length === 1 && questions[0].question_text.trim() === '') {
          setQuestions(uploadedQuestions);
        } else {
          setQuestions([...questions, ...uploadedQuestions]);
        }
        toast.success(`${uploadedQuestions.length} questions loaded. Review and click "Save Questions".`, { id: toastId });
      } else {
        toast.error('No questions found in file', { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to upload questions');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Question Name,Question Level,Question Type,Option_1,Qption_2,Option_3,Option_4,Ans,Ans_Desc\nWho is the father of computers?,Easy,Board,James Gosling,Charles Babbage,Dennis Ritchie,Bjarne Stroust,Charles Babbage,Charles Babbage is known as the father of computers.";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Quiz_Questions_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.topic_id || !formData.sub_topic_id) {
      toast.error("Please select Topic and Sub Topic.");
      return;
    }
    if (formData.question_type === 'Compititive' && !formData.exam_id) {
      toast.error("Please select an Exam.");
      return;
    }
    try {
      // Filter out empty ones
      const validQuestions = questions.filter(q => q.question_text.trim() !== '');
      if (validQuestions.length === 0) {
        toast.error("Please add at least one question.");
        return;
      }

      const toastId = toast.loading(`Saving ${validQuestions.length} questions...`);
      const questionsToSave = validQuestions.map(q => ({
        ...q,
        topic_id: formData.topic_id,
        sub_topic_id: formData.sub_topic_id,
        exam_id: formData.question_type === 'Compititive' ? (formData.exam_id || null) : null
      }));

      // Chunk in batches of 10 to avoid WAF / ModSecurity 403 Forbidden on large payloads
      const BATCH_SIZE = 10;
      let savedCount = 0;
      for (let i = 0; i < questionsToSave.length; i += BATCH_SIZE) {
        const batch = questionsToSave.slice(i, i + BATCH_SIZE);
        await api.post('/questions/bulk', { questions: batch });
        savedCount += batch.length;
        if (questionsToSave.length > BATCH_SIZE) {
          toast.loading(`Saving questions... (${savedCount}/${questionsToSave.length})`, { id: toastId });
        }
      }

      toast.success(`${savedCount} questions saved successfully`, { id: toastId });
      navigate('/config/questions');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save questions');
    }
  };

  return (
    <AppLayout title="Add Questions">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 60 }}>
        
        {/* Metadata */}
        <div style={{ background: '#fff', padding: 28, borderRadius: 16, border: `1px solid ${C.g200}` }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: C.g900 }}>Select Classification</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Type</label>
              <select value={formData.question_type} onChange={e => setFormData({...formData, question_type: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                <option value="Board Exams">Board Exams</option>
                <option value="Compititive">Compititive</option>
              </select>
            </div>
            
            {formData.question_type !== 'Compititive' ? (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Board</label>
                <select value={formData.board_id} onChange={e => setFormData({...formData, board_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                  <option value="">Select Board</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Exam</label>
                <select value={formData.exam_id} onChange={e => setFormData({...formData, exam_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                  <option value="">Select Exam</option>
                  {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Class</label>
              <select value={formData.class_id} onChange={e => setFormData({...formData, class_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Subject</label>
              <select value={formData.subject_id} onChange={e => setFormData({...formData, subject_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Topic</label>
              <select value={formData.topic_id} onChange={e => setFormData({...formData, topic_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                <option value="">Select Topic</option>
                {topics.filter(t => {
                  if (formData.question_type !== 'Compititive') {
                    if (t.exam_id) return false;
                    if (formData.board_id && t.board_id && t.board_id != formData.board_id) return false;
                  } else {
                    if (t.board_id) return false;
                    if (formData.exam_id && t.exam_id && t.exam_id != formData.exam_id) return false;
                  }
                  if (formData.class_id && t.class_id && t.class_id != formData.class_id) return false;
                  if (formData.subject_id && t.subject_id && t.subject_id != formData.subject_id) return false;
                  return true;
                }).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Sub Topic</label>
              <select value={formData.sub_topic_id} onChange={e => setFormData({...formData, sub_topic_id: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} required>
                <option value="">Select Sub Topic</option>
                {subTopics.filter(st => {
                  if (formData.topic_id && st.topic_id && st.topic_id != formData.topic_id) return false;
                  return true;
                }).map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Upload / Add */}
        <div style={{ background: '#fff', padding: 28, borderRadius: 16, border: `1px solid ${C.g200}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: C.g900 }}>Questions</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                <Download size={18} /> Template
              </button>
              <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" ref={directFileInputRef} onChange={handleDirectUpload} style={{ display: 'none' }} />
              <button type="button" onClick={() => directFileInputRef.current.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }} title="Uploads and saves questions directly into the database. Fastest and recommended.">
                <Upload size={18} /> Upload & Save Directly
              </button>
              <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
              <button type="button" onClick={() => fileInputRef.current.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: C.g50, color: C.g800, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }} title="Loads questions into form for review/manual edits before saving">
                <FileText size={18} /> Upload to Editor
              </button>
              <button type="button" onClick={addEmptyQuestion} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Plus size={16} /> Add Manual
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {questions.map((q, idx) => (
              <div key={idx} style={{ padding: 20, border: `1px solid ${C.g200}`, borderRadius: 12, background: C.g50, position: 'relative' }}>
                {questions.length > 1 && (
                  <button type="button" onClick={() => removeQuestion(idx)} style={{ position: 'absolute', top: 16, right: 16, background: '#FEF2F2', border: 'none', padding: 6, borderRadius: 6, color: C.danger, cursor: 'pointer' }}>
                    <Trash2 size={16} />
                  </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 16px' }}>
                  <h4 style={{ margin: 0, fontSize: 14, color: C.g700 }}>Question {idx + 1}</h4>
                  {(formData.topic_id || formData.sub_topic_id) && (
                    <span style={{ fontSize: 12, color: C.g500, background: '#fff', padding: '2px 8px', borderRadius: 4, border: `1px solid ${C.g200}` }}>
                      {topics.find(t => t.id == formData.topic_id)?.name || 'No Topic'} 
                      {formData.sub_topic_id && ` > ${subTopics.find(st => st.id == formData.sub_topic_id)?.name || ''}`}
                    </span>
                  )}
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Text</label>
                  <textarea value={q.question_text} onChange={e => updateQuestion(idx, 'question_text', e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: 8, border: `1px solid ${C.g200}`, minHeight: 80, outline: 'none' }} required />
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Image (Optional)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {q.image && (
                      <div style={{ width: 80, height: 80, borderRadius: 8, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
                        <img src={`${BASE_URL.replace('/api', '')}${q.image}`} alt="Question" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: C.g700 }}>
                      <ImageIcon size={16} /> Upload Image
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(idx, e)} style={{ display: 'none' }} />
                    </label>
                    {q.image && (
                      <button type="button" onClick={() => updateQuestion(idx, 'image', '')} style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Remove</button>
                    )}
                  </div>
                </div>

                {!['Subjective', 'Practical'].includes(q.format) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option A</label><input type="text" value={q.option_a} onChange={e => updateQuestion(idx, 'option_a', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option B</label><input type="text" value={q.option_b} onChange={e => updateQuestion(idx, 'option_b', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option C</label><input type="text" value={q.option_c} onChange={e => updateQuestion(idx, 'option_c', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option D</label><input type="text" value={q.option_d} onChange={e => updateQuestion(idx, 'option_d', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: ['Subjective', 'Practical'].includes(q.format) ? '1fr 1fr' : '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Format</label>
                    <select value={q.format || 'MCQ'} onChange={e => updateQuestion(idx, 'format', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="MCQ">MCQ</option><option value="Subjective">Subjective</option><option value="Practical">Practical</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Difficulty Level</label>
                    <select value={q.level} onChange={e => updateQuestion(idx, 'level', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="Any">Any</option><option value="Easy">Easy</option><option value="Medium">Medium</option><option value="Difficult">Difficult</option><option value="Hot">Hot</option>
                    </select>
                  </div>
                  {!['Subjective', 'Practical'].includes(q.format) && (
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Correct Answer</label>
                      <select value={q.correct_answer} onChange={e => updateQuestion(idx, 'correct_answer', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                        <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Answer Description (Optional)</label>
                  <div style={{ background: '#fff', borderRadius: 8 }}>
                    <ReactQuill theme="snow" value={q.answer_desc} onChange={val => updateQuestion(idx, 'answer_desc', val)} style={{ height: 120, marginBottom: 40 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" onClick={() => navigate('/config/questions')} style={{ padding: '12px 24px', background: C.g100, color: C.g700, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Save size={18} /> Save Questions
          </button>
        </div>

      </form>
    </AppLayout>
  );
};

export default QuestionForm;
